/**
 * Сверка смены с таблицей учёта при закрытии (план 155 Ф2) — общее для API и
 * воркера.
 *
 * ЗАЧЕМ ЗДЕСЬ. Смену закрывают три пути: кассир (API, `CloseShiftCommand`),
 * увольнение (API) и робот в 03:59 (воркер, сырой `pg`). Правило «какой итог
 * сверки у смены, закрытой без человека», исполнитель задачи и её текст —
 * одни на все три пути. Две реализации одной сверки разошлись бы (класс гочи
 * §96), поэтому SQL-выражения и тексты живут в одном месте.
 *
 * Снимок проверки (`cash_shifts.sheet_check`) — jsonb с ключами snake_case
 * (так его пишет API). Воркер читает его сырым `pg`, API — `::text` и
 * `JSON.parse` (красная линия 8), поэтому функции ниже принимают снимок в
 * snake_case и не зависят от CamelCasePlugin.
 *
 * ПЕРСОНАЛЬНЫЕ ДАННЫЕ. В снимке и в текстах нет клиентов: только метки групп
 * листа, группы CRM, числа и имена сотрудников.
 */

/** Исходы проверки, при которых закрытие в режиме «блокировка» не проходит. */
export const CASH_SHEET_BLOCKING_OUTCOMES = [
  'mismatch',
  'sheet_not_found',
  'format_error',
] as const;

export function isCashSheetBlockingOutcome(outcome: unknown): boolean {
  return (CASH_SHEET_BLOCKING_OUTCOMES as readonly unknown[]).includes(outcome);
}

/** Виды автозадач сверки (`client_tasks.auto_kind`). */
export const CASH_SHEET_TASK_MISMATCH = 'cash_sheet_mismatch';
export const CASH_SHEET_TASK_UNCHECKED = 'cash_sheet_unchecked';
export type CashSheetTaskKind = typeof CASH_SHEET_TASK_MISMATCH | typeof CASH_SHEET_TASK_UNCHECKED;

/** Виды уведомлений сверки — в каталоге `employee-notification-kinds.ts`. */
export const CASH_SHEET_CLOSE_BLOCKED = 'cash_sheet.close_blocked';
export const CASH_SHEET_CLOSE_PERMITTED = 'cash_sheet.close_permitted';
export const CASH_SHEET_CLOSE_EXCEPTION = 'cash_sheet.close_exception';
export const CASH_SHEET_SOURCE_BROKEN = 'cash_sheet.source_broken';

/** Минимальная длина причины разрешения и закрытия с расхождением (§2.6). */
export const CASH_SHEET_OVERRIDE_REASON_MIN = 10;

/**
 * SQL-выражение: итог сверки смены, закрытой без человека (робот в 03:59,
 * увольнение), по снимку последней проверки и режиму филиала (§2.7):
 *   - филиал без сверки или `off` — NULL («сверка не требовалась»);
 *   - последняя проверка показала расхождение (`mismatch`, `sheet_not_found`,
 *     `format_error`) — `mismatch`: известное расхождение не выдаём за «без
 *     сверки»;
 *   - проверок не было, последняя недоступна или совпала — `unchecked`:
 *     «сверено» только то, что проверено в момент закрытия.
 *
 * Аргументы — имена колонок из запроса вызывающего (константы кода, не ввод).
 */
export function unattendedSheetStatusSql(snapshotExpr: string, modeExpr: string): string {
  const blocking = CASH_SHEET_BLOCKING_OUTCOMES.map((o) => `'${o}'`).join(', ');
  return `CASE
    WHEN ${modeExpr} IS NULL OR ${modeExpr} = 'off' THEN NULL
    WHEN (${snapshotExpr})->>'outcome' IN (${blocking}) THEN 'mismatch'
    ELSE 'unchecked'
  END`;
}

/**
 * Действующая учётка (`m` — `tenant_memberships`, `e` — её `employees`):
 * членство активно, сотрудник работает и не служебный. `$1` — тенант.
 * `users.status` отдельно не проверяем: его зеркалит из HR-статуса
 * `syncAccountStatus` (`ManageAccountCommand`).
 */
const ACTIVE_MEMBERSHIP_FROM_SQL = `FROM tenant_memberships m
     JOIN employees e ON e.tenant_id = m.tenant_id AND e.id = m.employee_id
    WHERE m.tenant_id = $1::uuid
      AND m.status = 'active'
      AND e.status = 'active' AND e.is_system = false`;

/**
 * Учётка `m` — управляющий филиала `$2` по доступу: роль или вторичная роль
 * manager и филиал в её `branch_scopes` (их же сессия кладёт в `branch_ids`).
 * Пустой скоуп «все филиалы» значит только для boss — здесь он не подходит.
 */
const BRANCH_MANAGER_BY_SCOPE_SQL = `(m.role = 'manager' OR m.secondary_role = 'manager')
      AND EXISTS (SELECT 1 FROM branch_scopes bs
                   WHERE bs.tenant_id = m.tenant_id AND bs.membership_id = m.id
                     AND bs.branch_id = $2::uuid)`;

/**
 * Исполнитель задачи сверки и заявки на исправление — управляющий филиала
 * (план 150 Ф6, решение владельца 8; план 155 §2.6). Параметры: `$1` — тенант,
 * `$2` — филиал (может быть NULL), `$3` — актор события (может быть NULL).
 *
 * Управляющий «филиала» — по доступу, а не по штатной привязке: активная
 * учётка с ролью (или вторичной ролью) manager, у которой филиал в
 * `branch_scopes`. Сам актор, если он такой управляющий, — первым; иначе самый
 * давний по дате заведения. `owner_id` — самый давний активный владелец: его
 * берёт вызывающий, когда управляющего нет.
 *
 * Псевдонимы — в camelCase без подчёркиваний: одинаково читаются сырым `pg`
 * воркера и Kysely с CamelCasePlugin.
 */
export const BRANCH_MANAGER_SQL = `
SELECT
  (SELECT m.employee_id
     ${ACTIVE_MEMBERSHIP_FROM_SQL}
      AND ${BRANCH_MANAGER_BY_SCOPE_SQL}
    ORDER BY (m.employee_id = $3::uuid) DESC NULLS LAST, e.created_at, e.id
    LIMIT 1) AS "managerId",
  (SELECT o.id
     FROM employees o
    WHERE o.tenant_id = $1::uuid AND o.role = 'boss' AND o.status = 'active'
    ORDER BY o.created_at, o.id
    LIMIT 1) AS "ownerId"
`;

/**
 * Все управляющие филиала `$2` по доступу (та же выборка, что у
 * `BRANCH_MANAGER_SQL`, без `LIMIT 1`) и все Боссы по роли членства; `$1` —
 * тенант. Это ровно те, кто может подтвердить заявку филиала: роль сессии
 * берётся из членства, филиалы — из `branch_scopes` (план 168 §2.1, §2.5).
 */
export const BRANCH_MANAGERS_AND_BOSSES_SQL = `
SELECT DISTINCT m.employee_id AS "employeeId"
     ${ACTIVE_MEMBERSHIP_FROM_SQL}
      AND (m.role = 'boss' OR (${BRANCH_MANAGER_BY_SCOPE_SQL}))
`;

/** Активные владельцы по старшинству: `$1` — тенант. */
export const ACTIVE_OWNERS_SQL = `
SELECT o.id AS "id"
  FROM employees o
 WHERE o.tenant_id = $1::uuid AND o.role = 'boss' AND o.status = 'active'
 ORDER BY o.created_at, o.id
`;

// ---------------------------------------------------------------------------
// Снимок в snake_case — ровно те поля, которые нужны текстам.
// ---------------------------------------------------------------------------

export interface CashSheetSnapshotLike {
  outcome?: string | null;
  unavailable_reason?: string | null;
  error_kind?: string | null;
  message?: string | null;
  business_date?: string | null;
  fingerprint?: string | null;
  checked_at?: string | null;
  buckets?: Array<{
    crm_label?: string;
    crm_attended?: number;
    sheet_count?: number;
    diff?: number;
  }>;
  unmatched?: Array<{ label?: string; count?: number; blocking?: boolean }>;
  crm_groups?: Array<{ name?: string; attended?: number }>;
  money?: {
    lines?: Array<{
      key?: string;
      side?: string;
      crm_kopecks?: number;
      sheet_kopecks?: number | null;
      diff_kopecks?: number | null;
      blocking?: boolean;
    }>;
  };
  parse_errors?: Array<{ cell?: string | null; message?: string }>;
}

/** Кто и как закрыл смену с расхождением или без сверки. */
export interface CashSheetCloseActor {
  /** `actor` — старший сам; `permit` — по разрешению; `robot`; `dismissal`; `cashier`. */
  via: 'actor' | 'permit' | 'robot' | 'dismissal' | 'cashier';
  closedByName: string | null;
  overrideByName: string | null;
  overrideReason: string | null;
}

/** `2026-09-30` → `30.09`. */
export function cashSheetDayMonth(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m ? `${m[3]}.${m[2]}` : '';
}

/** `-100000` → `−1 000 ₽`, `50` → `+0,50 ₽`. */
export function cashSheetSignedRub(kopecks: number): string {
  const sign = kopecks > 0 ? '+' : kopecks < 0 ? '−' : '';
  return `${sign}${cashSheetRub(Math.abs(kopecks))}`;
}

export function cashSheetRub(kopecks: number): string {
  const abs = Math.abs(kopecks);
  const rub = Math.floor(abs / 100);
  const kop = abs % 100;
  const grouped = String(rub).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const body = kop ? `${grouped},${String(kop).padStart(2, '0')}` : grouped;
  return `${kopecks < 0 ? '−' : ''}${body} ₽`;
}

const SIDE_LABEL: Record<string, string> = { cash: 'нал', cashless: 'безнал' };
const MONEY_LABEL: Record<string, string> = {
  subscriptions_and_prepayments: 'Тек.месяц + Предоплаты',
  subscriptions: 'Тек.месяц',
  prepayments: 'Предоплаты',
};

/** Подпись строки денег: «Тек.месяц + Предоплаты», «Тек.месяц», «Предоплаты». */
export function cashSheetMoneyLabel(key: string): string {
  return MONEY_LABEL[key] ?? 'Тек.месяц + Предоплаты';
}

/** Блокирующие строки денег с разницей: «Тек.месяц + Предоплаты нал −1 000 ₽». */
export function cashSheetMoneyDiffs(
  snap: CashSheetSnapshotLike,
): Array<{ key: string; side: string; diff_kopecks: number }> {
  return (snap.money?.lines ?? [])
    .filter((l) => l.blocking && typeof l.diff_kopecks === 'number' && l.diff_kopecks !== 0)
    .map((l) => ({
      key: String(l.key ?? ''),
      side: String(l.side ?? ''),
      diff_kopecks: l.diff_kopecks as number,
    }));
}

/** Число расхождений в людях: корзины с разницей и несопоставленные строки. */
export function cashSheetGroupDiffCount(snap: CashSheetSnapshotLike): number {
  const buckets = (snap.buckets ?? []).filter((b) => (b.diff ?? 0) !== 0).length;
  const unmatched = (snap.unmatched ?? []).filter((u) => u.blocking).length;
  return buckets + unmatched;
}

function outcomeText(snap: CashSheetSnapshotLike): string {
  switch (snap.outcome) {
    case 'match':
      return 'совпадение';
    case 'mismatch':
      return 'расхождение';
    case 'sheet_not_found':
      return 'в таблице нет листа за эту дату';
    case 'format_error':
      return `лист не удалось разобрать${snap.message ? ` (${snap.message})` : ''}`;
    case 'unavailable':
      return snap.unavailable_reason === 'internal'
        ? 'сверка не выполнилась из-за сбоя CRM'
        : `таблица была недоступна${snap.message ? ` (${snap.message})` : ''}`;
    default:
      return 'проверок не было';
  }
}

/** Заголовок задачи сверки (§2.6). */
export function cashSheetTaskTitle(
  kind: CashSheetTaskKind,
  branchName: string,
  snap: CashSheetSnapshotLike | null,
): string {
  const day = cashSheetDayMonth(snap?.business_date);
  const where = [branchName, day].filter(Boolean).join(' ');
  if (kind === CASH_SHEET_TASK_MISMATCH) {
    return `Разобраться: сверка смены ${where} — расхождение`;
  }
  const why =
    snap?.outcome === 'unavailable' && snap.unavailable_reason === 'internal'
      ? 'сверка не выполнилась'
      : 'таблица была недоступна';
  return `Провести сверку за ${where}: ${why}`;
}

const MAX_LINES = 15;

/**
 * Описание задачи сверки: исход, группы и деньги с разницей (по правилу
 * «сумма» — только блокирующие строки), кто закрыл и кто разрешил. Для
 * «провести сверку» — цифры CRM из снимка, чтобы сравнить с листом глазами.
 */
export function cashSheetTaskDescription(
  kind: CashSheetTaskKind,
  snap: CashSheetSnapshotLike | null,
  who: CashSheetCloseActor,
): string {
  const lines: string[] = [];
  lines.push(`Исход сверки: ${snap ? outcomeText(snap) : 'проверок не было'}.`);

  if (snap && kind === CASH_SHEET_TASK_MISMATCH) {
    const groups = (snap.buckets ?? []).filter((b) => (b.diff ?? 0) !== 0);
    const unmatched = (snap.unmatched ?? []).filter((u) => u.blocking);
    if (groups.length || unmatched.length) lines.push('', 'Люди (отметки «Пришёл» / «кол-во»):');
    for (const b of groups.slice(0, MAX_LINES)) {
      lines.push(
        `• ${b.crm_label ?? 'группа'} — в CRM ${b.crm_attended ?? 0}, в таблице ${b.sheet_count ?? 0}`,
      );
    }
    for (const u of unmatched.slice(0, MAX_LINES)) {
      lines.push(`• строка таблицы «${u.label ?? ''}» (${u.count ?? 0}) — нет группы в CRM`);
    }
    const money = (snap.money?.lines ?? []).filter(
      (l) => l.blocking && typeof l.diff_kopecks === 'number' && l.diff_kopecks !== 0,
    );
    if (money.length) lines.push('', 'Деньги:');
    for (const l of money) {
      lines.push(
        `• ${cashSheetMoneyLabel(String(l.key))} ${SIDE_LABEL[String(l.side)] ?? ''} — ` +
          `в CRM ${cashSheetRub(l.crm_kopecks ?? 0)}, в таблице ${cashSheetRub(l.sheet_kopecks ?? 0)} ` +
          `(${cashSheetSignedRub(l.diff_kopecks as number)})`,
      );
    }
    for (const e of (snap.parse_errors ?? []).slice(0, 5)) {
      lines.push(`• ${e.cell ? `${e.cell}: ` : ''}${e.message ?? ''}`);
    }
  }

  if (snap && kind === CASH_SHEET_TASK_UNCHECKED) {
    const groups = (snap.crm_groups ?? []).filter((g) => (g.attended ?? 0) > 0);
    const money = (snap.money?.lines ?? []).filter((l) => l.blocking || l.key === 'subscriptions');
    if (snap.unavailable_reason === 'internal') {
      lines.push('', 'Цифры CRM не посчитались — сверьте лист с кассой смены вручную.');
    } else if (groups.length || money.length) {
      lines.push('', 'Цифры CRM на момент закрытия — сравните с листом:');
      for (const g of groups.slice(0, MAX_LINES)) {
        lines.push(`• ${g.name ?? 'группа'} — пришли ${g.attended ?? 0}`);
      }
      const shown = new Set<string>();
      for (const l of money) {
        const key = `${String(l.key)}.${String(l.side)}`;
        if (shown.has(key)) continue;
        shown.add(key);
        lines.push(
          `• ${cashSheetMoneyLabel(String(l.key))} ${SIDE_LABEL[String(l.side)] ?? ''} — ${cashSheetRub(l.crm_kopecks ?? 0)}`,
        );
      }
    }
  }

  lines.push('');
  switch (who.via) {
    case 'robot':
      lines.push(
        who.overrideByName
          ? `Смену закрыл робот в 03:59. Закрытие разрешил(а) ${who.overrideByName}: ${who.overrideReason ?? ''}`
          : kind === CASH_SHEET_TASK_MISMATCH
            ? 'Смену закрыл робот в 03:59, разрешения не было.'
            : 'Смену закрыл робот в 03:59.',
      );
      break;
    case 'dismissal':
      lines.push(
        `Смена закрыта при увольнении сотрудника${who.closedByName ? ` (${who.closedByName})` : ''}.`,
      );
      if (who.overrideByName) {
        lines.push(`Закрытие разрешил(а) ${who.overrideByName}: ${who.overrideReason ?? ''}`);
      }
      break;
    case 'actor':
      lines.push(
        `Закрыл(а) с расхождением ${who.closedByName ?? 'сотрудник'}. Причина: ${who.overrideReason ?? ''}`,
      );
      break;
    case 'permit':
      lines.push(`Закрыл(а) ${who.closedByName ?? 'сотрудник'}.`);
      lines.push(
        `Закрытие разрешил(а) ${who.overrideByName ?? 'сотрудник'}: ${who.overrideReason ?? ''}`,
      );
      break;
    case 'cashier':
      lines.push(`Закрыл(а) ${who.closedByName ?? 'сотрудник'}.`);
      break;
  }
  return lines.join('\n').trim();
}

/**
 * Задача сверки смены — одна на смену и вид (`client_tasks_auto_uk`), без
 * клиента, с филиалом смены. Срок — следующий день по времени филиала.
 * Параметры: `$1` тенант, `$2` заголовок, `$3` описание, `$4` исполнитель
 * (может быть NULL), `$5` вид (`cash_sheet_*`), `$6` смена, `$7` филиал.
 * Повтор (переоткрыли и закрыли снова) — no-op: RETURNING пуст.
 */
export const INSERT_CASH_SHEET_TASK_SQL = `
INSERT INTO client_tasks
  (tenant_id, title, description, status, due_date, assigned_to_employee_id,
   auto_kind, source_ref, cash_shift_id)
SELECT $1::uuid, $2::text, $3::text, 'open', (now() AT TIME ZONE b.timezone)::date + 1,
       $4::uuid, $5::text, $6::uuid, $6::uuid
  FROM branches b
 WHERE b.tenant_id = $1::uuid AND b.id = $7::uuid
ON CONFLICT (tenant_id, auto_kind, source_ref) DO NOTHING
RETURNING id AS "id", to_char(due_date, 'YYYY-MM-DD') AS "dueDate"
`;

/** Смена и филиал для уведомлений сверки (snake_case, как весь payload). */
export interface CashSheetShiftRef {
  shiftId: string;
  branchId: string;
  branchName: string;
  businessDate: string;
}

/**
 * payload уведомлений `cash_sheet.*`: смена, филиал, дата, исход и числа
 * расхождения (без клиентов). `extra` — поля конкретного вида: кто закрыл,
 * кто разрешил, id задачи.
 */
export function cashSheetNotificationPayload(
  ref: CashSheetShiftRef,
  snap: CashSheetSnapshotLike | null,
  extra: Record<string, unknown> = {},
): Record<string, unknown> {
  return {
    shift_id: ref.shiftId,
    branch_id: ref.branchId,
    branch_name: ref.branchName,
    business_date: ref.businessDate,
    outcome: snap?.outcome ?? null,
    unavailable_reason: snap?.unavailable_reason ?? null,
    fingerprint: snap?.fingerprint ?? null,
    group_diff_count: snap ? cashSheetGroupDiffCount(snap) : 0,
    money_diffs: snap ? cashSheetMoneyDiffs(snap) : [],
    ...extra,
  };
}

/**
 * SQL-выражение: разрешение старшего переживает закрытие без человека, только
 * если оно выдано ровно на расхождение последней проверки (§2.7). Иначе
 * колонки разрешения обнуляются — плашка «разрешил(а)» у такой смены врала бы.
 */
export function unattendedKeepsPermitSql(
  snapshotExpr: string,
  modeExpr: string,
  fingerprintExpr: string,
): string {
  const blocking = CASH_SHEET_BLOCKING_OUTCOMES.map((o) => `'${o}'`).join(', ');
  return `(${modeExpr} = 'enforce'
    AND (${snapshotExpr})->>'outcome' IN (${blocking})
    AND ${fingerprintExpr} IS NOT NULL
    AND ${fingerprintExpr} = (${snapshotExpr})->>'fingerprint')`;
}
